import { useEffect, useState } from "react";
import { api, errText } from "@/lib/api";
import { toast } from "sonner";
import { Check, Sparkles, TrendingUp, Coins, Users } from "lucide-react";

const TIER_COLORS = {
  access: "#a0a0ab",
  standard: "#d4af37",
  green: "#39ff14",
  professional: "#00f0ff",
};

export function Plans() {
  const [plans, setPlans] = useState([]);
  const [me, setMe] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/plans").then((r)=>setPlans(r.data.plans)).catch(()=>{});
    api.get("/auth/me").then((r)=>setMe(r.data)).catch(()=>{});
  }, []);

  const subscribe = async (plan) => {
    const lookup = cycle === "monthly" ? plan.monthly_lookup : plan.annual_lookup;
    setBusy(plan.id);
    try {
      const { data } = await api.post("/stripe/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errText(e)); setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="plans-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">Membership Plans</h1>
          <p className="text-white/60 text-sm mt-2">Each tier grants a percentage of the annual share premium — index-linked to global FX, StarCoin, and daily virtual-finance profits.</p>
        </div>
        <div className="inline-flex bg-white/5 rounded-full p-1 border border-white/10" data-testid="plan-cycle">
          <button onClick={()=>setCycle("monthly")} data-testid="cycle-monthly" className={`px-4 py-1.5 rounded-full text-sm ${cycle==="monthly" ? "bg-[#d4af37] text-black" : "text-white/70"}`}>Monthly</button>
          <button onClick={()=>setCycle("annual")} data-testid="cycle-annual" className={`px-4 py-1.5 rounded-full text-sm ${cycle==="annual" ? "bg-[#d4af37] text-black" : "text-white/70"}`}>Annual <span className="text-[10px] ml-1 opacity-70">(save ~17%)</span></button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        {plans.map((p) => {
          const price = cycle === "monthly" ? p.monthly : p.annual;
          const period = cycle === "monthly" ? "/mo" : "/yr";
          const current = me?.plan_id === p.id;
          return (
            <div key={p.id} className="panel p-6 flex flex-col" style={{borderColor: `${TIER_COLORS[p.id]}55`}} data-testid={`plan-${p.id}`}>
              <div className="chip mb-3" style={{color:TIER_COLORS[p.id], borderColor:`${TIER_COLORS[p.id]}55`}}>{p.name.toUpperCase()}</div>
              <div className="font-display text-5xl" style={{color:TIER_COLORS[p.id]}}>${price}<span className="text-white/40 text-lg font-mono">{period}</span></div>
              <div className="mt-3 font-mono text-sm text-[#d4af37]" data-testid={`plan-${p.id}-share`}>◆ {p.share_pct}% share allocation</div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.perks.map((k) => (
                  <li key={k} className="flex gap-2 text-sm text-white/70"><Check size={14} className="text-[#39ff14] shrink-0 mt-1"/> {k}</li>
                ))}
              </ul>
              {current ? (
                <div className="btn-ghost mt-6 text-center text-xs" data-testid={`plan-${p.id}-current`}>Current plan</div>
              ) : (
                <button data-testid={`plan-${p.id}-subscribe`} onClick={()=>subscribe(p)} disabled={busy===p.id} className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2">
                  <Sparkles size={14}/>{busy===p.id ? "Redirecting…" : "Subscribe"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      <div className="panel p-4 text-xs text-white/50 font-mono">
        Test card <span className="text-[#00f0ff]">4242 4242 4242 4242</span> · any future expiry · any CVC
      </div>
    </div>
  );
}

export function Shares() {
  const [alloc, setAlloc] = useState(null);
  useEffect(() => {
    const load = () => api.get("/shares/allocation").then((r)=>setAlloc(r.data)).catch(()=>{});
    load(); const t = setInterval(load, 12000); return () => clearInterval(t);
  }, []);
  return (
    <div className="space-y-6" data-testid="shares-panel">
      <div>
        <h1 className="font-display text-3xl">Shares & Dividends</h1>
        <p className="text-white/60 text-sm mt-2">Your share of the BlackStar annual premium index — driven by global FX movement, StarCoin single-currency, and profits from daily virtual-finance transactions.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="panel p-6" data-testid="shares-plan">
          <div className="label">Your plan</div>
          <div className="font-display text-3xl mt-2 text-[#d4af37] capitalize">{alloc?.plan_id || "free"}</div>
          <div className="font-mono text-sm text-white/60 mt-2">Share allocation: <b className="text-[#39ff14]">{alloc?.share_pct?.toFixed(2) ?? "0.00"}%</b></div>
        </div>
        <div className="panel p-6" data-testid="shares-daily">
          <div className="label">Daily dividend</div>
          <div className="font-display text-3xl mt-2 text-[#39ff14]">${alloc?.daily_dividend_usd?.toFixed(4) ?? "…"}</div>
          <div className="font-mono text-xs text-white/50 mt-2">Accrues 24/7 · paid quarterly</div>
        </div>
        <div className="panel p-6" data-testid="shares-annual">
          <div className="label">Annual dividend projection</div>
          <div className="font-display text-3xl mt-2 text-[#00f0ff]">${alloc?.annual_dividend_usd?.toLocaleString(undefined,{minimumFractionDigits:2, maximumFractionDigits:2}) ?? "…"}</div>
          <div className="font-mono text-xs text-white/50 mt-2">At current premium index level</div>
        </div>
      </div>

      <div className="panel p-6" data-testid="index-panel">
        <div className="flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-[#d4af37]"/><span className="label">Annual Premium Index</span></div>
        <div className="font-display text-5xl text-[#d4af37]" data-testid="index-total">${alloc?.index?.annual_premium_index_usd?.toLocaleString(undefined,{maximumFractionDigits:2}) ?? "…"}</div>
        <div className="grid md:grid-cols-3 gap-3 mt-6">
          <ComponentCard icon={<Coins size={14}/>} label="FX basket · population weighted" value={alloc?.index?.components?.fx_basket_usd} pct={alloc && ((alloc.index.components.fx_basket_usd/alloc.index.annual_premium_index_usd)*100)}/>
          <ComponentCard icon={<Sparkles size={14}/>} label="StarCoin single currency" value={alloc?.index?.components?.starcoin_usd} pct={alloc && ((alloc.index.components.starcoin_usd/alloc.index.annual_premium_index_usd)*100)}/>
          <ComponentCard icon={<TrendingUp size={14}/>} label="Daily virtual finance profits × 365" value={alloc?.index?.components?.txn_flow_usd} pct={alloc && ((alloc.index.components.txn_flow_usd/alloc.index.annual_premium_index_usd)*100)}/>
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4 text-xs font-mono text-white/50">
          <div>Daily profit: <span className="text-[#39ff14]">${alloc?.index?.daily_profit_usd?.toFixed(2) ?? "…"}</span></div>
          <div>24h gross flow: <span className="text-[#d4af37]">${alloc?.index?.gross_24h_usd?.toLocaleString(undefined,{maximumFractionDigits:2}) ?? "…"}</span></div>
          <div className="inline-flex items-center gap-1"><Users size={11}/> <span>Active shareholders: <span className="text-[#00f0ff]">{alloc?.index?.active_shareholders ?? "…"}</span></span></div>
        </div>
      </div>
    </div>
  );
}

function ComponentCard({ icon, label, value, pct }) {
  return (
    <div className="p-4 border border-white/5 rounded-lg">
      <div className="flex items-center gap-2 text-white/60 text-xs">{icon}{label}</div>
      <div className="font-display text-xl text-[#d4af37] mt-2">${value?.toLocaleString(undefined,{maximumFractionDigits:2}) ?? "…"}</div>
      <div className="h-1 bg-white/5 rounded-full overflow-hidden mt-3">
        <div className="h-full bg-gradient-to-r from-[#d4af37] to-[#39ff14]" style={{ width: `${pct || 0}%` }}/>
      </div>
      <div className="text-[10px] font-mono text-white/40 mt-1">{pct?.toFixed(1) ?? "…"}% of index</div>
    </div>
  );
}
